import React from "react";
import { Link, useParams } from "react-router-dom";
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CSpinner } from "@coreui/react";

import { JobPauseResumeButton } from "@src/components/JobPauseResumeButton/JobPauseResumeButton";
import { JobStatusBadge } from "@src/components/JobStatusBadge";
import { JobTypeBadge } from "@src/components/JobTypeBadge";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useLiveJob } from "@src/hooks/useLiveJob";
import { formatDateTimeShort } from "@src/utils/dateUtils";
import { formatDurationSeconds, getJobSummary } from "@src/utils/formatters";
import { isCoordinatorJob } from "@src/utils/jobChildren";
import { sanitizeJobForModal } from "@src/utils/jobDisplay";
import { getPausedTrials, isStopAcknowledged } from "@src/utils/jobPauseResume";
import { getEntityLink, getJobCreatedEntityId, getJobCreatedEntityLabel } from "@src/utils/routing";
import { JobChildrenPanel } from "./JobChildrenPanel";

const dash = <span className="text-body-secondary">—</span>;

/** One label/value line, matching the run detail card's summary rows. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="SummaryItem">
    <strong>{label}:</strong>
    <span>{children}</span>
  </div>
);

const timestamp = (value?: string) => (value ? formatDateTimeShort(value) : dash);

/** JSON block with its own scroll, so a large config cannot stretch the page. */
const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => (
  <pre className="JobDetailJson mb-0">{JSON.stringify(value, null, 2)}</pre>
);

/**
 * Full record of a single job: its settings, its result, and the children it spawned when it is a
 * coordinator. Reached from the jobs list and from any link that needs to point at a job.
 */
export const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showJsonModal } = useJsonModal();

  const jobId = Number(id);
  const invalidId = !id || Number.isNaN(jobId);
  const live = useLiveJob(invalidId ? Number.NaN : jobId);
  const { job, applyJob } = live;
  const loading = !invalidId && live.loading;
  const error = invalidId ? `Invalid job id "${id}"` : live.error;

  if (loading) {
    return (
      <div className="JobDetailPage d-flex align-items-center gap-2 p-4">
        <CSpinner size="sm" /> Loading job {id}...
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="JobDetailPage p-4">
        <CAlert color="danger">{error ?? `Job ${id} not found`}</CAlert>
        <Link to="/jobs">Back to jobs</Link>
      </div>
    );
  }

  const createdEntityId = getJobCreatedEntityId(job);
  const pausedTrials = getPausedTrials(job);

  return (
    <div className="JobDetailPage">
      <CCard className="JobDetailPageSummary mb-3">
        <CCardHeader className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2">
          <h5 className="mb-0">
            #{job.id} -{" "}
            {getJobSummary(job.job_type_name, job.exchange_name, job.symbol_name, job.period)}
            <CButton
              color="info"
              variant="ghost"
              size="sm"
              className="ms-2"
              onClick={() => showJsonModal(sanitizeJobForModal(job), `Full job ${job.id}`)}
              title="View full job record"
            >
              ℹ️
            </CButton>
          </h5>
          <div className="ms-lg-auto d-flex align-items-center gap-2">
            <JobTypeBadge jobType={job.job_type_name} />
            <JobStatusBadge status={job.status_name} runningState={job.running_state} />
            {pausedTrials !== undefined && (
              <small className="JobDetailPausedTrials text-body-secondary">
                {isStopAcknowledged(job)
                  ? `${pausedTrials} trials done`
                  : `stopping, ${pausedTrials} trials so far`}
              </small>
            )}
            <JobPauseResumeButton job={job} onUpdated={applyJob} />
            {createdEntityId && (
              <Link to={getEntityLink(job.job_type_name, createdEntityId)}>
                {getJobCreatedEntityLabel(job.job_type_name)} {createdEntityId} 🔗
              </Link>
            )}
          </div>
        </CCardHeader>
        <CCardBody>
          <div className="RunDetailSummary">
            <div className="summaryColumn summaryColumn-info">
              <Field label="Description">{job.description || dash}</Field>
              <Field label="Worker">{job.worker_id ? <code>{job.worker_id}</code> : dash}</Field>
              <Field label="Priority">{job.priority ?? dash}</Field>
              <Field label="Origin">
                {job.metadata?.origin ? <code>{job.metadata.origin.type}</code> : dash}
              </Field>
            </div>
            <div className="summaryColumn summaryColumn-timing">
              <Field label="Created">{timestamp(job.create_ts)}</Field>
              <Field label="Claimed">{timestamp(job.claim_ts)}</Field>
              <Field label="Started">{timestamp(job.start_ts)}</Field>
              <Field label="Completed">{timestamp(job.complete_ts)}</Field>
              <Field label="Duration">
                {job.start_ts && job.complete_ts
                  ? formatDurationSeconds(job.start_ts, job.complete_ts)
                  : dash}
              </Field>
            </div>
          </div>
        </CCardBody>
      </CCard>

      {job.error_message && (
        <CAlert color="danger" className="JobDetailError">
          {job.error_message}
        </CAlert>
      )}

      {isCoordinatorJob(job) && <JobChildrenPanel job={job} />}

      <CCard className="JobDetailConfig mb-3">
        <CCardHeader>
          <h5 className="mb-0">Config</h5>
        </CCardHeader>
        <CCardBody>
          <JsonBlock value={job.config} />
        </CCardBody>
      </CCard>

      <CCard className="JobDetailResult mb-3">
        <CCardHeader>
          <h5 className="mb-0">Result</h5>
        </CCardHeader>
        <CCardBody>
          {job.result ? <JsonBlock value={sanitizeJobForModal(job).result} /> : dash}
        </CCardBody>
      </CCard>
    </div>
  );
};
